import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { UpdateInsuranceTransactionDto } from "../../../../../../api-client";

/**
 * Hook for handling billing operations (print, export)
 */
export const useBillingOperations = () => {
  const TransactionStore = useStore(TransactionsContext);
  const {
    submitEditedBillingData,
    printPaperBillingDocument,
    updateExportBillingStatusAndRxStatus,
  } = TransactionStore;

  /**
   * Helper function to create DTO from bill
   */
  const createBillingDataDto = (
    bill: any,
    notes: Record<string, string>
  ): UpdateInsuranceTransactionDto => {
    const { payerIK, accountInfo, data, userInfo, wasExported } = bill;
    const { rxs, payerDto, totalPrice } = data;

    const rxsIds = rxs.map((rx: any) => rx.id).filter((id: any): id is string => !!id);
    const paperCenter = payerDto?.paperReceivingCenter?.ik;
    const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

    return {
      payerId: payerIK,
      accountId: accountInfo.id,
      amount: totalPrice,
      userId: userInfo.id!,
      rxIds: rxsIds,
      paperCenterId: paperCenter || undefined,
      dataCenterId: digitalCenter || undefined,
      notes: notes[bill.billId] || "",
      wasExported,
      tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
    };
  };

  /**
   * Generic function to process multiple bills with a callback
   */
  const processBills = async (
    bills: any[],
    notes: Record<string, string>,
    processor: (billId: string, dto: UpdateInsuranceTransactionDto) => Promise<any>
  ) => {
    const updatePromises = bills.map(async (bill: any) => {
      const dto = createBillingDataDto(bill, notes);
      return processor(bill.billId, dto);
    });
    return Promise.all(updatePromises);
  };

  /**
   * Print bills
   */
  const handlePrint = async (
    bills: any[],
    notes: Record<string, string>,
    setBills: React.Dispatch<React.SetStateAction<any[]>>,
    onSuccess: () => void
  ) => {
    try {
      const responses = await processBills(bills, notes, printPaperBillingDocument);

      // Update bills with print responses
      responses.forEach((response, index) => {
        if (response && response.docDto) {
          const billId = bills[index].billId;
          setBills((prevBills: any[]) =>
            prevBills.map((bill: any) => {
              if (bill.billId === billId) {
                return {
                  ...bill,
                  docDto: response.docDto!,
                };
              }
              return bill;
            })
          );
        }
      });

      notificationStore.showMessage("Urbelegbegleitschreiben wurde erfolgreich als PDF erstellt.", "success");
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message;
      console.error("Error Printing Bills", error);
      notificationStore.showMessage(
        `Beim Drucken der Rechnung ist ein Problem aufgetreten: Grund -> ${errorMessage}`,
        "error"
      );
    }
  };

  /**
   * Export bills
   */
  const handleExport = async (bills: any[], onSuccess: () => void) => {
    try {
      // Update export status directly
      const statusUpdatePromises = bills.map((bill: any) => {
        const rxIds = bill.data.rxs.map((rx: any) => rx.id).filter((id: any): id is string => !!id);
        return updateExportBillingStatusAndRxStatus(
          bill.billId,
          {
            wasExported: true,
            billStatus: "OPEN" as any, // Avoid import for now
          },
          rxIds
        );
      });

      await Promise.all(statusUpdatePromises);
      onSuccess();
    } catch (error) {
      console.error("Error exporting transactions", error);
      notificationStore.showMessage("Fehler beim Exportieren der Rechnungen", "error");
    }
  };

  return {
    handlePrint,
    handleExport,
    createBillingDataDto,
    processBills,
    submitEditedBillingData,
    TransactionStore,
  };
};
